/**
 * @ngdoc service
 * @name tokenService
 * @module flowingly.runner.services
 *
 * @description Service responsible for managing tokens
 */
declare const moment;

import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';
import { Guid } from '@Shared.Angular/@types/guid';
import { Services } from '@Shared.Angular/@types/services';
import angular from 'angular';
// Service responsible for persisting user information using angular-storage

export interface ITenantBusiness {
  name: string;
  id: Guid;
}

angular.module('flowingly.services').factory('tokenService', tokenService);

tokenService.$inject = [
  '$rootScope',
  '$interval',
  '$cookies',
  '$location',
  'APP_CONFIG',
  'store',
  'jwtHelper',
  'angularAuth0',
  'momentService',
  'authLoggingApiService',
  'browserUtilsService',
  'angularAuth0SpaSdk',
  'flowinglyConstants',
  'pubsubService',
  'appInsightsService'
];

function tokenService(
  $rootScope: angular.IRootScopeService,
  $interval: angular.IIntervalService,
  $cookies,
  $location: angular.ILocationService,
  APP_CONFIG: Services.APP_CONFIG,
  store,
  jwtHelper,
  angularAuth0,
  momentService: Moment,
  authLoggingApiService: Services.AuthLoggingApiService,
  browserUtilsService: Services.BrowserUtilsService,
  angularAuth0SpaSdk: SharedAngular.AngularAuth0SpaSdk,
  flowinglyConstants,
  pubsubService: SharedAngular.PubSubService,
  appInsightsService: SharedAngular.AppInsightsService
) {
  // We are using a different namespace for Development, Staging and Production (see web.config)
  const nameSpace = 'flowingly.' + APP_CONFIG.flowinglyEnvironment;
  const tokenStoreName = nameSpace + '.token';
  const lastLoginStoreName = nameSpace + '.lastlogin';
  const ngidelExpiryName = 'ngIdle.expiry';
  const TRACE_PREFIX = 'tokenService';

  // Try to get token from angular-storage first, then fallback to direct localStorage
  // This is needed for iframe context where angular-storage might not work properly
  let _token = store.get(tokenStoreName);
  if (!_token) {
    try {
      const directToken = localStorage.getItem(tokenStoreName);
      if (directToken) {
        // Check if it's JSON-wrapped (angular-storage format) or raw string
        _token =
          directToken.startsWith('"') && directToken.endsWith('"')
            ? JSON.parse(directToken)
            : directToken;
      }
    } catch (e) {
      console.error(
        'tokenService - Failed to read token from localStorage during init',
        e
      );
    }
  }

  let _unauthorised = false;
  let inLogoutProcess = false;
  let checkTokenExpiredPromise: Promise<string>;
  let checkTokenExpiredPending = false;
  // Flag to skip token refresh logic (for iframe popup auth where Auth0 SDK refresh fails)
  let skipTokenRefresh = false;
  try {
    // Auto-detect iframe + popup login mode
    const isInIframe =
      window !== window.parent ||
      window.frameElement !== null ||
      window.self !== window.top;
    if (
      isInIframe &&
      APP_CONFIG.enableIframePopupLogin &&
      APP_CONFIG.auth0UseRefreshToken
    ) {
      skipTokenRefresh = true;
    }
  } catch (e) {
    // Cross-origin iframe - also skip token refresh
    if (APP_CONFIG.enableIframePopupLogin && APP_CONFIG.auth0UseRefreshToken) {
      skipTokenRefresh = true;
    }
  }

  const service = {
    getPermissions: getPermissions,
    getTenant: getTenant,
    getUserId: getUserId,
    getToken: getToken,
    checkTokenExpire: checkTokenExpire,
    clearToken: clearToken,
    refreshTokenIfPermissionsDonotExist: refreshTokenIfPermissionsDonotExist,
    setToken: setToken,
    getLastLogin: getLastLogin,
    clearLastLogin: clearLastLogin,
    setIdleExpiry: setIdleExpiry,
    manualIdleCheck: manualIdleCheck,
    isAllApplicationIdle: isAllApplicationIdle,
    setUnauthorisedAccess: setUnauthorisedAccess,
    wasUnauthorisedAccess: wasUnauthorisedAccess,
    setInLogoutProcess: setInLogoutProcess,
    isTokenExpired: isTokenExpired,
    setSkipTokenRefresh: setSkipTokenRefresh
  };

  return service;

  //////////// Public API Methods

  function setSkipTokenRefresh(skip: boolean) {
    skipTokenRefresh = skip;
  }

  function setInLogoutProcess(val) {
    inLogoutProcess = val;
  }

  function setUnauthorisedAccess(access) {
    _unauthorised = access;
  }

  function wasUnauthorisedAccess() {
    return _unauthorised;
  }

  function getPermissions() {
    if (_token) {
      return jwtHelper.decodeToken(_token).flowinglypermissions as string[];
    }
  }

  function getUserId(): Guid | null {
    if (_token) {
      try {
        const tokenData = jwtHelper.decodeToken(_token);
        return tokenData.nameidentifier;
      } catch (e) {
        console.error('tokenService - Failed to decode token', e);
        return null;
      }
    }
    return null;
  }

  function getTenant(): ITenantBusiness | null {
    if (_token) {
      try {
        const tokenData = jwtHelper.decodeToken(_token);
        return {
          id: tokenData.businessidentifier,
          name: tokenData.businessname
        };
      } catch (e) {
        console.error('tokenService - Failed to decode token', e);
        return null;
      }
    }
    return null;
  }

  function getToken() {
    // If skipTokenRefresh is set and we have an in-memory token, use it
    if (skipTokenRefresh && _token) {
      return _token;
    }

    // Try angular-storage first
    _token = store.get(tokenStoreName);

    // If that fails and skipTokenRefresh is set, try direct localStorage
    if (!_token && skipTokenRefresh) {
      try {
        const directToken = localStorage.getItem(tokenStoreName);
        if (directToken) {
          _token =
            directToken.startsWith('"') && directToken.endsWith('"')
              ? JSON.parse(directToken)
              : directToken;
        }
      } catch (e) {
        console.error(
          'tokenService - Failed to read token from localStorage',
          e
        );
      }
    }

    return _token;
  }

  function isAllApplicationIdle() {
    if (browserUtilsService.isApp()) {
      return false;
    }
    const idleExpiry = $cookies.get(ngidelExpiryName);

    if (
      idleExpiry &&
      momentService.utc(idleExpiry).isBefore(momentService.utc())
    )
      return true;

    return false;
  }

  function manualIdleCheck() {
    const idleExpiry = $cookies.get(ngidelExpiryName),
      interval = momentService(idleExpiry) - momentService() + 2000;

    console.log(`manualIdleCheck after seconds Web: ${interval / 1000}`);

    if (interval > 0) {
      $interval(
        () => {
          $rootScope.$broadcast('IdleStart');
        },
        interval,
        1
      );
    }
  }

  function refreshTokenIfPermissionsDonotExist(isRequestUserCondition = false) {
    const domainPort =
      window.location.port !== '' ? `:${window.location.port}` : '';
    const currentDomain = `${window.location.protocol}//${window.location.hostname}${domainPort}`;

    authLoggingApiService.log(
      'tokenService.ts refreshTokenIfPermissionsDonotExist() called'
    );
    const checkoldTokenPromise = new Promise((resolve, reject) => {
      // for invite user, shouldn't look local storage token at all!
      if (
        $location.path().toLowerCase() === '/acceptinvite' &&
        $location.search().inviteToken &&
        !isRequestUserCondition
      ) {
        resolve(undefined);
      } else {
        const token = getToken();

        if (token) {
          // if token expired, don't refresh it
          if (isAllApplicationIdle() && !inLogoutProcess) {
            clearToken(`${TRACE_PREFIX}-refreshTokenIfPermissionsDonotExist`);
            authLoggingApiService.log(
              'tokenService.ts refreshTokenIfPermissionsDonotExist() - rejecting with message Error: session expired! and sending re-dire-ct-url as /flowsactive'
            );
            reject({
              error: 'Error: session expired!',
              redirectUri: currentDomain + '/flowsactive/'
            });
          }

          // Skip permission check if skipTokenRefresh flag is set (iframe popup auth)
          if (skipTokenRefresh) {
            resolve(undefined);
            return;
          }

          if (!getPermissions()) {
            if (isAuth0AppV2(token)) {
              renewTokenAuth0SpaSdk(
                resolve,
                reject,
                'refreshTokenIfPermissionsDonotExist()'
              );
            } else {
              angularAuth0.checkSession(
                {
                  responseType: 'id_token token',
                  clientID: APP_CONFIG.auth0ClientId,
                  redirectUri: currentDomain + '/flowsactive/'
                },
                function (err, delegationResult) {
                  if (delegationResult) {
                    setToken(delegationResult.idToken);
                    authLoggingApiService.log(
                      'tokenService.ts refreshTokenIfPermissionsDonotExist() resolving with id token'
                    );
                    resolve(delegationResult.idToken);
                  } else {
                    authLoggingApiService.log(
                      'tokenService.ts refreshTokenIfPermissionsDonotExist() rejecting with message Error: Refresh of the token failed!'
                    );
                    reject('Error: Refresh of the token failed!');
                  }
                }
              );
            }
          } else {
            authLoggingApiService.log(
              'tokenService.ts refreshTokenIfPermissionsDonotExist() rejecting with message undefined'
            );
            resolve(undefined); // Token was not expired
          }
        } else {
          authLoggingApiService.log(
            'tokenService.ts refreshTokenIfPermissionsDonotExist() rejecting with message Missing token'
          );
          resolve({ error: 'Missing token' });
        }
      }
    });

    return checkoldTokenPromise;
  }

  function setToken(token, savelogin = false) {
    authLoggingApiService.log('tokenService.ts setToken() called');

    _token = token;
    store.set(tokenStoreName, _token);

    // Also store in direct localStorage for iframe context (JSON-wrapped for angular-storage compatibility)
    try {
      if (token) {
        localStorage.setItem(tokenStoreName, JSON.stringify(token));
      } else {
        appInsightsService.trackTrace(`${TRACE_PREFIX}-clearingToken`, {
          reason: 'setToken called with falsy token value'
        });
        localStorage.removeItem(tokenStoreName);
      }
    } catch (e) {
      console.error('tokenService - Failed to write token to localStorage', e);
    }

    if (savelogin) {
      setLastLogin(token);
    }
    setIdleExpiry();
  }

  function setLastLogin(token) {
    const connection = jwtHelper.decodeToken(token)['connection'] || null;
    store.set(lastLoginStoreName, { connection: connection });
  }

  function getLastLogin() {
    return store.get(lastLoginStoreName);
  }

  function clearLastLogin() {
    store.remove(lastLoginStoreName);
  }

  function setIdleExpiry() {
    const expireTime = moment
      .utc()
      .add(APP_CONFIG.sessionTimeoutInSecond, 's')
      .toISOString();
    $cookies.put(ngidelExpiryName, expireTime);
    pubsubService.publish('IDLE_EXPIRY_CHANGED', expireTime);
  }

  function clearToken(reason: string) {
    try {
      appInsightsService.trackTrace(`${TRACE_PREFIX}-clearingToken`, {
        reason
      });
    } catch (e) {
      console.error(
        'tokenService - Failed to track token clearing in app insights',
        e
      );
    }

    store.remove(tokenStoreName);
    // Also remove direct localStorage key (used in iframe context)
    try {
      localStorage.removeItem(tokenStoreName);
    } catch (e) {
      console.error(
        'tokenService - Failed to remove token from localStorage',
        e
      );
    }
    _token = undefined;
  }

  // Returns new token if it is refreshed, else returns null
  // If there is an error it rejects the promise
  function checkTokenExpire(redirectUri?: string) {
    authLoggingApiService.log('tokenService.ts checkTokenExpire() called');
    if (checkTokenExpiredPending) {
      return checkTokenExpiredPromise;
    }
    checkTokenExpiredPending = true;
    checkTokenExpiredPromise = new Promise((resolve, reject) => {
      const token = getToken();
      if (!token) {
        // If skipTokenRefresh is set, use in-memory token if available
        if (skipTokenRefresh && _token) {
          resolve(null);
          checkTokenExpiredPending = false;
          return;
        }
        authLoggingApiService.log(
          'tokenService.ts checkTokenExpire() rejecting with message Error: Missing token'
        );
        reject('Error: Missing token');
        checkTokenExpiredPending = false;
        return;
      }

      // If skipTokenRefresh is set, still validate token expiry but skip SDK-based refresh.
      // If the token is expired, reject so callers can force re-login.
      if (skipTokenRefresh) {
        if (!isTokenExpired(token)) {
          resolve(null);
          checkTokenExpiredPending = false;
          return;
        }
        // Token is expired — in iframe popup mode we can't silently refresh,
        // so clear the token and reject to trigger re-login.
        authLoggingApiService.log(
          'tokenService.ts checkTokenExpire() - skipTokenRefresh mode: token is expired, clearing and rejecting'
        );
        clearToken(`${TRACE_PREFIX}-checkTokenExpire`);
        reject('Error: Token expired in iframe popup mode');
        checkTokenExpiredPending = false;
        return;
      }

      if (!isTokenExpired(token)) {
        authLoggingApiService.log(
          'tokenService.ts checkTokenExpire() resolving with message null'
        );
        resolve(null); // Token was not expired
        checkTokenExpiredPending = false;
        return;
      }

      if (isAuth0AppV2(token)) {
        renewTokenAuth0SpaSdk(resolve, reject);
      } else {
        const domainPort =
          window.location.port !== '' ? `:${window.location.port}` : '';
        const currentDomain = `${window.location.protocol}//${window.location.hostname}${domainPort}`;
        angularAuth0.checkSession(
          {
            responseType: 'id_token token',
            clientID: APP_CONFIG.auth0ClientId,
            redirectUri: currentDomain + (redirectUri || '')
          },
          function (err, delegationResult) {
            if (delegationResult) {
              setToken(delegationResult.idToken);
              authLoggingApiService.log(
                'tokenService.ts checkTokenExpire() resolving with id token'
              );
              resolve(delegationResult.idToken);
            } else {
              authLoggingApiService.log(
                'tokenService.ts checkTokenExpire() checkSession Error: ' +
                  JSON.stringify(err)
              );
              reject('Error: Refresh of the token failed!');
            }
            checkTokenExpiredPending = false;
          }
        );
      }
    });

    return checkTokenExpiredPromise;
  }

  async function renewTokenAuth0SpaSdk(
    resolve: (value: string | PromiseLike<string>) => void,
    reject: (reason?: any) => void,
    caller = 'checkTokenExpire()'
  ) {
    if (
      window.location.pathname === '/modeler/' &&
      APP_CONFIG.flowinglyEnvironment ===
        flowinglyConstants.flowinglyEnvironments.Local
    ) {
      console.error(
        'Please close this page, refresh runner, and reopen modeler.This issue occurs only in local enviornment. Since the refresh token saved in local storage by runner cannot be used by modeler for token renewal, as it is running on a different port.'
      );
      return;
    }

    try {
      const auth0Client = await angularAuth0SpaSdk.getClient();
      await auth0Client.getTokenSilently({
        detailedResponse: true,
        cacheMode: 'off'
      });
      const claims = await auth0Client.getIdTokenClaims();
      const idToken = claims.__raw;
      setToken(idToken);
      checkTokenExpiredPending = false;
      authLoggingApiService.log(
        `tokenService.ts ${caller} resolving with id token`
      );
      resolve(idToken);
    } catch (error) {
      checkTokenExpiredPending = false;
      reject('Error: Refresh of the token failed!');
    }
  }

  function isTokenExpired(token = getToken()) {
    const willExpireInSeconds = 45;
    return (
      jwtHelper.getTokenExpirationDate(token) - new Date().getTime() <
      willExpireInSeconds * 1000
    );
  }

  function isAuth0AppV2(token: string) {
    const clientIdInToken = jwtHelper.decodeToken(token).aud;
    return clientIdInToken === APP_CONFIG.auth0AppV2ClientId;
  }
}

export type TokenServiceType = ReturnType<typeof tokenService>;
